import { KeyboardIcon, MicIcon } from "./icons";

type DeviceType = "mic" | "midi" | "keyboard";

/**
 * Three-way state mirrored from the studio toggle hooks. `unavailable` means
 * the underlying API is denied / unsupported, the badge stays clickable so
 * the click can still trigger a permission request when relevant.
 */
type DeviceState = "on" | "off" | "unavailable";

type DeviceStatusBadgeProps = {
  type: DeviceType;
  state: DeviceState;
  deviceName?: string;
  /** When provided, the badge becomes a button (toggles the device on/off, or opens settings). */
  onClick?: () => void;
  /**
   * Classes ajoutées à la pastille elle-même.
   *
   * Pour qu'un appelant puisse la retirer en CSS (`hidden` sous une requête
   * média) sans l'envelopper : un `<span>` intercalé dans une rangée flex
   * reprendrait le `display` de la pastille, et `hidden` se battrait alors
   * avec lui sur la même propriété — l'ordre des règles de la feuille, et non
   * celui des classes, déciderait de ce qui se voit.
   */
  className?: string;
};

function MidiIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="7.5" r="1" fill="currentColor" />
      <circle cx="7.5" cy="12" r="1" fill="currentColor" />
      <circle cx="16.5" cy="12" r="1" fill="currentColor" />
      <circle cx="9" cy="16" r="1" fill="currentColor" />
      <circle cx="15" cy="16" r="1" fill="currentColor" />
    </svg>
  );
}

const TYPE_LABEL: Record<DeviceType, string> = {
  mic: "Micro",
  midi: "MIDI",
  keyboard: "Clavier",
};

function stateLabel(type: DeviceType, state: DeviceState): string {
  if (state === "unavailable") {
    return "non disponible";
  }

  if (type === "midi") {
    return state === "on" ? "connecté" : "déconnecté";
  }

  return state === "on" ? "actif" : "inactif";
}

function buildTooltip(type: DeviceType, state: DeviceState, deviceName?: string): string {
  const label = TYPE_LABEL[type];
  const value = stateLabel(type, state);

  if (deviceName) {

    return `${label} : ${deviceName}, ${value}`;
  }

  return `${label}, ${value}`;
}

const ON_CLASSES: Record<DeviceType, string> = {
  mic: "bg-emerald-500/20 text-emerald-400 ring-1 ring-inset ring-emerald-500/30",
  midi: "bg-purple-500/20 text-purple-300 ring-1 ring-inset ring-purple-500/30",
  keyboard: "bg-amber-500/20 text-amber-300 ring-1 ring-inset ring-amber-500/30",
};

const OFF_CLASSES = "bg-bg-elevated/40 text-text-muted ring-1 ring-inset ring-white/5";
const UNAVAILABLE_CLASSES =
  "bg-bg-elevated/20 text-text-muted/50 ring-1 ring-inset ring-white/5 opacity-60";

export function DeviceStatusBadge({ type, state, deviceName, onClick, className = "" }: DeviceStatusBadgeProps) {
  // Même traitement que `IconButton` en `sm` : au doigt, la cible passe à
  // 44 px de haut par un pseudo-élément, sans que la pastille grossisse.
  // L'écart horizontal reste minime pour qu'aucune pastille ne recouvre sa
  // voisine, l'axe qui manque vraiment au doigt étant le vertical.
  const base =
    "relative inline-flex items-center justify-center w-7 h-7 rounded-md transition-colors duration-150 select-none pointer-coarse:after:absolute pointer-coarse:after:content-[''] pointer-coarse:after:-inset-y-2 pointer-coarse:after:-inset-x-0.5";

  const stateClasses =
    state === "on" ? ON_CLASSES[type] : state === "off" ? OFF_CLASSES : UNAVAILABLE_CLASSES;

  const tooltip = buildTooltip(type, state, deviceName);
  const icon =
    type === "mic" ? (
      <MicIcon className="w-3.5 h-3.5" />
    ) : type === "midi" ? (
      <MidiIcon className="w-3.5 h-3.5" />
    ) : (
      <KeyboardIcon className="w-3.5 h-3.5" />
    );

  // Diagonal slash overlay when the device API is unavailable, makes the
  // "permission denied / unsupported" case visually distinct from a plain off.
  const overlay =
    state === "unavailable" ? (
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 flex items-center justify-center"
      >
        <span className="block h-px w-4 rotate-45 bg-current opacity-70" />
      </span>
    ) : null;

  if (onClick) {

    return (
      <button
        type="button"
        onClick={onClick}
        aria-label={tooltip}
        title={tooltip}
        className={`${base} ${stateClasses} cursor-pointer hover:brightness-125 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${className}`}
      >
        {icon}
        {overlay}
      </button>
    );
  }

  return (
    <span
      role="status"
      aria-label={tooltip}
      title={tooltip}
      className={`${base} ${stateClasses} ${className}`}
    >
      {icon}
      {overlay}
    </span>
  );
}
